import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ModalComponent from './Modal';
import { showLoadingMessage, showSuccessMessage, showWarningMessage } from '../successMessage';

const splitTrainerName = (name = '') => {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    const lastName = parts.length > 1 ? parts.pop() : '';
    return { firstName: parts.join(' '), lastName };
};

const formatTrainerName = (name = '') => String(name).toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());

const addOneYear = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(`${dateValue}T00:00:00`);
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString().slice(0, 10);
};

export const UploadCard = ({ title, includeCompletionDate = false, includeRenewalDate = false, categoryOptions = [], dateFieldLabel = 'Date of Completion', showUploadDate = false, onEdit = () => {} }) => {
    const [files, setFiles] = useState([]);
    const [showUploadPopup, setShowUploadPopup] = useState(false);
    const [pendingFile, setPendingFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [category, setCategory] = useState('');
    const [completionDate, setCompletionDate] = useState('');
    const [uploadError, setUploadError] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);

    const selectFile = (file) => {
        if (!file) return;
        onEdit();
        setPendingFile(file);
        setFileName(file.name);
        setUploadError('');
    };

    const addFile = () => {
        if (!pendingFile || !fileName.trim() || (categoryOptions.length > 0 && !category) || (includeCompletionDate && !completionDate)) {
            setUploadError(`Please complete all mandatory upload details and add a document.`);
            return;
        }
        showLoadingMessage('Saving Document');
        setFiles((current) => [...current, {
            id: `${pendingFile.name}-${pendingFile.lastModified}-${Math.random()}`,
            name: fileName.trim(),
            category,
            originalName: pendingFile.name,
            size: pendingFile.size,
            uploadedDate: new Date().toLocaleDateString('en-AU'),
            completionDate: includeCompletionDate ? completionDate : '',
            renewalDate: includeRenewalDate ? addOneYear(completionDate) : '',
        }]);
        setShowUploadPopup(false);
        setPendingFile(null);
        setFileName('');
        setCategory('');
        setCompletionDate('');
        setUploadError('');
        setShowSuccess(true);
        window.setTimeout(() => showSuccessMessage('Document saved successfully'), 0);
        window.setTimeout(() => setShowSuccess(false), 2200);
    };

    return (
        <section className='trainerProfileCard'>
            <div className='trainerProfileCardHeader'><h3>{title} <span className='trainerRequiredMark'>*</span></h3></div>
            <div className='trainerUploadControls'>
                <button type='button' className='trainerFileUploadButton' onClick={() => { onEdit(); setShowUploadPopup(true); }}>
                    <svg aria-hidden='true' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><path d='M12 3v12'/><path d='m7 8 5-5 5 5'/><path d='M5 21h14'/></svg>
                    File
                </button>
            </div>
            <div className='trainerUploadTableWrap'>
                <table className='trainerProfileSubtable'>
                    <thead><tr><th>File Name</th>{showUploadDate && <th>Date</th>}{includeCompletionDate && <th>{dateFieldLabel}</th>}{includeRenewalDate && <th>Renewal Date</th>}<th>Action</th></tr></thead>
                    <tbody>
                        {files.length ? files.map((file) => (
                            <tr key={file.id}>
                                <td>{file.name}</td>
                                {showUploadDate && <td>{file.uploadedDate}</td>}
                                {includeCompletionDate && <td>{file.completionDate || '—'}</td>}
                                {includeRenewalDate && <td>{file.renewalDate || '—'}</td>}
                                <td><button type='button' className='trainerUploadRemove' onClick={() => { showWarningMessage(`Remove ${file.name}?`); if (window.confirm(`Remove ${file.name}? This action cannot be undone.`)) { onEdit(); setFiles((current) => current.filter((item) => item.id !== file.id)); } }}>Remove</button></td>
                            </tr>
                        )) : <tr><td colSpan={2 + (showUploadDate ? 1 : 0) + (includeCompletionDate ? 1 : 0) + (includeRenewalDate ? 1 : 0)} className='trainerEmptyTable'>No files uploaded</td></tr>}
                    </tbody>
                </table>
            </div>
            {showUploadPopup && createPortal((
                <div className='trainerUploadPopupOverlay' onClick={(event) => event.stopPropagation()}>
                    <div className='trainerUploadPopup'>
                        <div className='trainerUploadPopupHeader'>
                            <h3>Upload {title}</h3>
                            <div className='trainerUploadPopupHeaderActions'>
                                <button className='trainerUploadPopupSave' type='button' onClick={addFile}>Save</button>
                                <button className='trainerUploadPopupClose' type='button' onClick={() => setShowUploadPopup(false)}>Close</button>
                            </div>
                        </div>
                        <div className='trainerUploadPopupBody'>
                            {categoryOptions.length > 0 && (
                                <label>Document Type <span className='trainerRequiredMark'>*</span>
                                    <select value={category} onChange={(event) => setCategory(event.target.value)}>
                                        <option value=''>Select document type</option>
                                        {categoryOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                                    </select>
                                </label>
                            )}
                            <label>File Name <span className='trainerRequiredMark'>*</span><input value={fileName} onChange={(event) => setFileName(event.target.value)} placeholder='Enter file name' /></label>
                            {includeCompletionDate && <label>{dateFieldLabel} <span className='trainerRequiredMark'>*</span><input type='date' value={completionDate} onChange={(event) => setCompletionDate(event.target.value)} /></label>}
                            <label
                                className={`trainerFileDropzone ${pendingFile ? 'hasFile' : ''}`}
                                onDragOver={(event) => event.preventDefault()}
                                onDrop={(event) => { event.preventDefault(); selectFile(event.dataTransfer.files?.[0]); }}
                            >
                                <svg aria-hidden='true' width='25' height='25' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><path d='M12 3v12'/><path d='m7 8 5-5 5 5'/><path d='M5 21h14'/></svg>
                                <strong>{pendingFile ? pendingFile.name : 'Drop or drag document here'}</strong>
                                <span>or click to browse</span>
                                <input type='file' onChange={(event) => { selectFile(event.target.files?.[0]); event.target.value = ''; }} />
                            </label>
                            {uploadError && <p className='trainerProfileValidation'>{uploadError}</p>}
                        </div>
                    </div>
                </div>
            ), document.body)}
            {showSuccess && <div className='trainerUploadSuccess' role='status'><span>✓</span><strong>Document added successfully</strong></div>}
        </section>
    );
};

const TrainerProfileModal = ({ trainer, onClose, onComplianceChange, embedded = false }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [saveNotice, setSaveNotice] = useState(null);
    const [details, setDetails] = useState({ firstName: '', lastName: '', email: '', dateOfBirth: '', contactNumber: '' });

    useEffect(() => {
        const names = splitTrainerName(trainer?.name);
        setDetails({
            firstName: names.firstName,
            lastName: names.lastName,
            email: trainer?.email || '',
            dateOfBirth: trainer?.dob || trainer?.dateOfBirth || '',
            contactNumber: trainer?.contactNumber || trainer?.phone || '',
        });
        setIsEditing(false);
        setSaveError('');
    }, [trainer]);

    const startEditing = () => setIsEditing(true);
    const updateDetail = (field, value) => {
        startEditing();
        setDetails((current) => ({ ...current, [field]: value }));
        setSaveError('');
    };
    const saveProfile = () => {
        const isIncomplete = Object.values(details).some((value) => !String(value || '').trim());
        setSaveError('');
        showLoadingMessage('Saving Trainer Profile');
        setIsEditing(false);
        if (isIncomplete) {
            onComplianceChange?.(trainer, 'pending');
            setSaveNotice({ type: 'warning', detail: 'Not all fields completed.' });
            window.setTimeout(() => showWarningMessage('Trainer profile saved with incomplete fields.'), 0);
        } else {
            setSaveNotice({ type: 'success', detail: 'All profile fields completed.' });
            window.setTimeout(() => showSuccessMessage('Trainer profile saved successfully'), 0);
        }
        window.setTimeout(() => setSaveNotice(null), 2600);
    };
    const updateCompliance = (status) => {
        onComplianceChange?.(trainer, status);
        setSaveNotice({
            type: 'success',
            title: 'Compliance Status Updated.',
            detail: status === 'compliant' ? 'Trainer marked Compliant.' : 'Trainer marked Non-compliant.',
        });
    };

    const profileBody = (
        <>
            {trainer && <>
                <div className='trainerProfileModalHeader'>
                    <div><h2>Trainer Profile - {formatTrainerName(trainer.name)}</h2></div>
                    <div className='trainerProfileModalActions'>
                        <div className='trainerProfilePrimaryActions'>
                            <button className={`trainerProfileEditButton ${isEditing ? 'saving' : ''}`} type='button' aria-label={isEditing ? 'Save trainer profile' : 'Edit trainer profile'} title={isEditing ? 'Save' : 'Edit'} onClick={isEditing ? saveProfile : startEditing}>
                                {isEditing ? 'Save' : 'Edit'}
                            </button>
                            <button className='trainerProfileCloseButton' type='button' onClick={onClose}>{embedded ? 'Back to Trainers' : 'Close'}</button>
                        </div>
                        <div className='trainerProfileComplianceActions'>
                            <button className='trainerMarkCompliant' type='button' onClick={() => updateCompliance('compliant')}>Compliant</button>
                            <button className='trainerMarkNonCompliant' type='button' onClick={() => updateCompliance('nonCompliant')}>Non-compliant</button>
                        </div>
                    </div>
                </div>
                <div className='trainerProfileModalBody' onClick={startEditing}>
                    <section className='trainerProfileCard'>
                        <div className='trainerProfileCardHeader'><h3>Details <span className='trainerRequiredMark'>*</span></h3></div>
                        <div className='trainerDetailsGrid'>
                            <label>First Name <span className='trainerRequiredMark'>*</span><input required value={details.firstName} readOnly={!isEditing} onFocus={startEditing} onChange={(event) => updateDetail('firstName', event.target.value)} /></label>
                            <label>Last Name <span className='trainerRequiredMark'>*</span><input required value={details.lastName} readOnly={!isEditing} onFocus={startEditing} onChange={(event) => updateDetail('lastName', event.target.value)} /></label>
                            <label>Email <span className='trainerRequiredMark'>*</span><input required type='email' value={details.email} readOnly={!isEditing} onFocus={startEditing} onChange={(event) => updateDetail('email', event.target.value)} /></label>
                            <label>Date of Birth <span className='trainerRequiredMark'>*</span><input required type='date' value={details.dateOfBirth} readOnly={!isEditing} onFocus={startEditing} onChange={(event) => updateDetail('dateOfBirth', event.target.value)} /></label>
                            <label>Contact Number <span className='trainerRequiredMark'>*</span><input required value={details.contactNumber} readOnly={!isEditing} onFocus={startEditing} onChange={(event) => updateDetail('contactNumber', event.target.value)} /></label>
                        </div>
                        {saveError && <p className='trainerProfileValidation'>{saveError}</p>}
                    </section>
                    <section className='trainerComplianceGroup'>
                        <div className='trainerComplianceSectionHeader'>
                            <h3>Compliance</h3>
                            <p>Trainer compliance documents and currency records.</p>
                        </div>
                        <UploadCard title='Resume' showUploadDate onEdit={startEditing} />
                        <UploadCard title='VET Currency' includeCompletionDate includeRenewalDate categoryOptions={['Professional Development', 'VET Membership', 'Other']} onEdit={startEditing} />
                        <UploadCard title='Industry Currency' includeCompletionDate onEdit={startEditing} />
                        <UploadCard title='Trainer Matrix' onEdit={startEditing} />
                        <UploadCard title='WWCC' showUploadDate onEdit={startEditing} />
                        <UploadCard title='Qualifications' includeCompletionDate dateFieldLabel='Date of Completion' onEdit={startEditing} />
                        <UploadCard title='OTHER' showUploadDate onEdit={startEditing} />
                    </section>
                    <section className='trainerProfileCard'>
                        <div className='trainerProfileCardHeader'><h3>Audit Log</h3></div>
                        <div className='trainerUploadTableWrap'>
                            <table className='trainerProfileSubtable'>
                                <thead><tr><th>Date/Time</th><th>By Who</th><th>Comments</th></tr></thead>
                                <tbody><tr><td colSpan='3' className='trainerEmptyTable'>No audit activity</td></tr></tbody>
                            </table>
                        </div>
                    </section>
                </div>
                {saveNotice && createPortal(
                    <div className={`trainerProfileSaveNotice ${saveNotice.type}`} role='status'>
                        <strong>{saveNotice.title || 'Trainer Profile Saved.'}</strong>
                        <span>{saveNotice.detail}</span>
                        <button type='button' onClick={() => { setSaveNotice(null); onClose(); }}>Close</button>
                    </div>,
                    document.body
                )}
            </>}
        </>
    );

    return embedded
        ? <section className='trainerProfileInline'>{profileBody}</section>
        : <ModalComponent
            showModal={Boolean(trainer)}
            setShowModal={(open) => { if (!open) onClose(); }}
            contentClassName='trainerProfileModal'
            overlayClassName='trainerProfileModalOverlay'
            contentStyle={{ width: '90%', maxWidth: '980px', maxHeight: '88vh', overflowY: 'auto', padding: 0, border: '1px solid #bfcbd6', borderRadius: '14px', background: '#f8fafc', boxShadow: '0 28px 70px rgba(15, 23, 42, 0.28)' }}
        >{profileBody}</ModalComponent>;
};

export default TrainerProfileModal;
